import { DestroyRef, EventEmitter, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { IAzureSSOAuthModel } from '@client-models/auth.model';
import { LOCAL_STORAGE_CONSTANT } from '@constants/localstorage.constant';
import { AvailablePartnersResponse, loginDbModel, LoginResponse, PartnerInfo, ResetPasswordTokenResponse } from '@db-models/login-db.model';
import { PartnerDbModel } from '@db-models/partner-db.model';
import { WorkerDbModel } from '@db-models/worker-db.model';
import { PartnerService } from '@feature-services/partner.service';
import { PermissionService } from '@feature-services/permission.service';
import { ReleaseNotesService } from '@feature-services/release-notes.service';
import { StartSiteService } from '@feature-services/start-site.service';
import { SubscriptionService } from '@feature-services/subscription.service';
import { TimezoneService } from '@feature-services/timezone.service';
import { WorkerService } from '@feature-services/worker.service';
import { AlertToastrService } from '@util-services/alert-toastr.service';
import { CalioEventsService } from '@util-services/calio-events.service';
import { CryptoService } from '@util-services/crypto.service';
import { DialogService } from '@util-services/dialog.service';
import { HttpClientService } from '@util-services/http-client.service';
import { LocalStorageService } from '@util-services/local-storage.service';
import { Buffer } from 'buffer';
import moment from 'moment';
import { Observable, combineLatest, finalize } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  userIsLoggedInEvent = new EventEmitter<WorkerDbModel>();
  userLogOutEvent = new EventEmitter<void>();
  partnerSwitchedEvent = new EventEmitter<void>();
  workerAvatarEvent = new EventEmitter<string>();
  resetLoginFormEvent = new EventEmitter<boolean>();
  resetAuthMessageEvent = new EventEmitter<boolean>();
  #currentDate = new Date();
  queryparams: {
    [name: string]: string;
  };

  private localStorageService = inject(LocalStorageService);
  private router = inject(Router);
  private cryptoService = inject(CryptoService);
  private httpClientService = inject(HttpClientService);
  private releaseNotesService = inject(ReleaseNotesService);
  private alertToastrService = inject(AlertToastrService);
  private workerService = inject(WorkerService);
  private startSiteService = inject(StartSiteService);
  private partnerService = inject(PartnerService);
  private subscriptionService = inject(SubscriptionService);
  private dialogService = inject(DialogService);
  private permissionService = inject(PermissionService);
  private timezoneService = inject(TimezoneService);
  private destroyRef = inject(DestroyRef);
  private calioEventsService = inject(CalioEventsService);

  constructor() {
    this.queryparams = window.location.search.substring(1).split('&').reduce(function (q, query) {
      const chunks = query.split('=');
      const key = chunks[0];
      const value = chunks[1];
      return (q[key] = value, q);
    }, {});

    this.cryptoService.logoutEvent.subscribe({
      next: (redirectionLink: string) => this.logout({ redirect: redirectionLink, 'login-required': true })
    });
  }

  hasValidAuthToken(): boolean {
    const authToken = this.getAuthTokenFromLocalStorage();
    if (authToken && this.validateAuthToken(authToken)) {
      return true;
    }
    return false;
  }

  validateAuthToken(authToken: string): boolean {
    try {
      const timeObject = JSON.parse(Buffer.from(authToken?.split('.')[1], 'base64')?.toString('utf-8'));
      const currentTime = moment().unix();
      if (timeObject?.exp <= currentTime || !timeObject) { // if authToken expired
        throw Error();
      }
      return true;
    } catch {
      return false;
    }
  }

  serverAndDashboardLogout(): void {
    // Check if a token exists.
    // - If a token is present, perform server-side logout.
    // - Otherwise, proceed with normal logout.
    if (this.getAuthTokenFromLocalStorage()) {
      this.serverLogout()
        .pipe(
          finalize(() => this.logout()),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    } else {
      this.logout();
    }
  }

  logout(queryParams = {}): void {
    this.calioEventsService.closeCalioDialogEvent.emit();
    this.localStorageService.clear();
    this.workerService.clearLoggedInWorkerCache();
    if (Object.keys(queryParams).length) {
      this.router.navigate(['/auth', 'login'], { queryParams: queryParams });
    } else {
      this.router.navigateByUrl(`/auth/login`);
    }

    // To remove Reseller banner in app component.
    this.userLogOutEvent.emit();
  }

  login(): void {
    this.router.navigateByUrl('/auth/login');
  }

  loginUser(body: { email: string, password: string }): Observable<LoginResponse> {
    return this.httpClientService.authServicePost('auth/signin', body, {});
  }

  selectPartner(selectionToken: string, partnerUuid: string): Observable<loginDbModel> {
    const body = { selection_token: selectionToken, partner_uuid: partnerUuid };
    return this.httpClientService.authServicePost('auth/select_partner', body, {});
  }

  getAvailablePartners(): Observable<AvailablePartnersResponse> {
    return this.httpClientService.authServiceGet('auth/available_partners', {});
  }

  switchPartner(partnerUuid: string): Observable<loginDbModel> {
    const body = { partner_uuid: partnerUuid };
    return this.httpClientService.authServicePost('auth/switch_partner', body, {});
  }

  handlePartnerSwitch(loginResponse: loginDbModel): void {
    this.localStorageService.clear();
    this.workerService.clearLoggedInWorkerCache();
    this.partnerSwitchedEvent.emit();

    // Store token appropriately based on MFA status
    if (loginResponse.is_mfa_enabled) {
      // Store as MFA token - MFA component will read this
      this.setMfaTokenInLocalStorage(loginResponse.token);
    } else {
      // Store as auth token - direct access to dashboard
      this.setAuthTokenInLocalStorage(loginResponse.token);
    }

    this.handleAuthCallback(
      loginResponse.permissions,
      loginResponse.worker_uuid,
      !!+loginResponse?.is_mfa_enabled,
      '/',
      'switch'
    );
  }

  isMultiPartnerResponse(response: LoginResponse): response is { requires_partner_selection: true; selection_token: string; partners: PartnerInfo[] } {
    return 'requires_partner_selection' in response && response.requires_partner_selection === true;
  }

  forgotPassword(email: string): Observable<boolean> {
    const body = { email: email };
    return this.httpClientService.authServicePost('auth/forgot_password', body, {});
  }

  resetPassword(token: string, password: string, partnerUuids?: string[]): Observable<boolean> {
    const body: { token: string; password: string; partner_uuids?: string[] } = { token, password };
    if (partnerUuids?.length) {
      body.partner_uuids = partnerUuids;
    }
    return this.httpClientService.authServicePost('auth/reset_password', body, {});
  }

  validateResetPasswordToken(token: string): Observable<ResetPasswordTokenResponse> {
    return this.httpClientService.authServiceGet(`auth/verify_reset_password_token/`, { params: { token: token } });
  }

  setAuthCallback(loginUser: loginDbModel, isRedirectionUrl: string, loginWith: string): void {
    this.handleAuthCallback(loginUser.permissions, loginUser.worker_uuid, !!+loginUser?.is_mfa_enabled, isRedirectionUrl, loginWith);
  }

  private handleAuthCallback(permissions: string[], workerUuid: string, is_mfa_enabled = false, isRedirectionUrl = null, loginWith: string) {
    this.resetLocalStorage();

    if (is_mfa_enabled) {
      if (isRedirectionUrl && !isRedirectionUrl?.includes('mfa')) {
        this.router.navigate(['/auth', 'mfa'], { queryParams: { 'redirect': isRedirectionUrl } });
      } else {
        this.router.navigateByUrl('auth/mfa');
      }
    } else {
      if (permissions?.length) {
        this.permissionService.setUserPermissions(permissions);
      } else {
        this.alertToastrService.showError('general.user_roles_not_defined');
        setTimeout(() => this.logout(), 2000);
      }

      combineLatest([
        this.partnerService.initializeDashboard(),
        this.workerService.setLoggedInUserInLocalStorage(workerUuid),
        this.startSiteService.getStartSiteStatsByYear(this.#currentDate.getFullYear()),
        this.partnerService.validateConfig(),
        this.subscriptionService.getFeaturesListAndStoreInLocalStorage(),
      ])
        .subscribe({
          next: ([partnerData, worker, statisticsData]) => {
            const partnerFormLocalStorage = partnerData.partner;
            const totalBookings = statisticsData.total_bookings_ever.appointments + statisticsData.total_bookings_ever.events;
            worker?.avatar && this.localStorageService.set(LOCAL_STORAGE_CONSTANT.AVATAR_KEY, worker.avatar);
            this.localStorageService.set(LOCAL_STORAGE_CONSTANT.CURRENT_NUMBER_OF_BOOKINGS_KEY, this.cryptoService.encryptValue(totalBookings.toString()));
            this.timezoneService.getTimezones().subscribe();
            if (isRedirectionUrl) {
              this.router.navigateByUrl(isRedirectionUrl);
            } else if (
              partnerFormLocalStorage &&
              partnerFormLocalStorage?.is_setup_done !== 1 &&
              partnerFormLocalStorage?.is_onboarding_call_completed === false
            ) {
              this.router.navigateByUrl('/almost-there');
            } else if (
              partnerFormLocalStorage &&
              partnerFormLocalStorage?.is_setup_done === 0
            ) {
              this.router.navigate(['onboard'], {
                queryParams: {
                  step: partnerFormLocalStorage.current_setup_step
                }
              }).then(() => {
                if (partnerFormLocalStorage.current_setup_step > 1) {
                  this.alertToastrService.showSuccess('onboard.resume_process_message');
                }
              });
            } else {
              this.router.navigateByUrl('/').then(() => {
                this.openPartnerReleaseNotesDialog(partnerFormLocalStorage);
              });
            }

            this.userIsLoggedInEvent.emit(worker);
          },
          error: () => {
            this.localStorageService.clear();
            this.workerService.clearLoggedInWorkerCache();
            this.router.navigate(['/auth', loginWith], { queryParams: { 'something-went-wrong': true } });
            this.resetLoginFormEvent.emit();
          }
        });
    }
  }

  openPartnerReleaseNotesDialog(partner: PartnerDbModel): void {
    if (this.permissionService.hasPermission('read:partners-release-notes') && partner?.has_release_notes) {
      this.releaseNotesService.getPartnerReleaseNotes().subscribe({
        next: releaseNotes => {
          !releaseNotes && (releaseNotes = []);

          if (releaseNotes?.length) {
            this.dialogService.openPartnerReleaseNotesDialog({ releaseNotes },
              'release_notes_comp.release_notes_card_title',
              'release_notes_comp.partner_release_notes_dialog_subtitle'
            ).afterClosed().subscribe(() => this.deletePartnerReleaseNote());
          }
        },
      });
    }
  }

  deletePartnerReleaseNote(): void {
    this.releaseNotesService.deletePartnerReleaseNote().subscribe();
  }

  resetLocalStorage(): void {
    this.localStorageService.remove(LOCAL_STORAGE_CONSTANT.SHORT_APPOINTMENT_SERVICE_LIST_KEY);
    this.localStorageService.remove(LOCAL_STORAGE_CONSTANT.SHORT_WORKER_LIST_KEY);
    this.localStorageService.remove(LOCAL_STORAGE_CONSTANT.SCHEDULER_AVAILABILITES_KEY);
    this.localStorageService.remove(LOCAL_STORAGE_CONSTANT.WORKERS_KEY);
    this.localStorageService.remove(LOCAL_STORAGE_CONSTANT.LOGGED_IN_WORKER);
    this.localStorageService.remove(LOCAL_STORAGE_CONSTANT.USER_PERMISSIONS);
  }

  setAuthTokenInLocalStorage(authToken: string): void {
    const encryptedString = this.cryptoService.encryptValue(authToken);
    this.localStorageService.set(LOCAL_STORAGE_CONSTANT.CALENSO_AUTH_TOKEN, encryptedString);
  }

  setMfaTokenInLocalStorage(mfaToken: string): void {
    const encryptedString = this.cryptoService.encryptValue(mfaToken);
    this.localStorageService.set(LOCAL_STORAGE_CONSTANT.CALESNO_MFA_TOKEN, encryptedString);
  }

  getMfaTokenFromLocalStorage(): string {
    const encrypted: string = this.localStorageService.get(LOCAL_STORAGE_CONSTANT.CALESNO_MFA_TOKEN);
    return encrypted ? this.cryptoService.decryptValue(encrypted) : null;
  }

  getAuthTokenFromLocalStorage(): string {
    const encrypted: string = this.localStorageService.get(LOCAL_STORAGE_CONSTANT.CALENSO_AUTH_TOKEN);
    return encrypted ? this.cryptoService.decryptValue(encrypted) : null;
  }

  handleAzureSsoLogin(payload: IAzureSSOAuthModel): Observable<loginDbModel> {
    return this.httpClientService.authServicePost('auth/signin', payload, {});
  }

  resendVerifyEmail(partnerUuid: string): Observable<{ success: boolean }> {
    return this.httpClientService.authServicePost(`partners/send_verification_email/${partnerUuid}`, {}, {});
  }

  verifyPartnerToken(body: { token: string }): Observable<{ success: boolean }> {
    return this.httpClientService.authServicePost(`partners/verify`, body, {});
  }

  serverLogout(): Observable<{ success: boolean }> {
    return this.httpClientService.authServiceGet('auth/signout', {});
  }

  forceLogoutWorker(workerUuid: string): Observable<{ success: boolean }> {
    return this.httpClientService.authServicePost(`workers/force_logout/${workerUuid}`, {}, {});
  }

  unblockWorker(workerUuid: string): Observable<{ success: boolean }> {
    return this.httpClientService.authServicePost(`workers/unblock/${workerUuid}`, {}, {});
  }
}
